import { useEffect, useState } from 'react';
import BusinessIcon from '@mui/icons-material/Business';
import ReceiptIcon from '@mui/icons-material/Receipt';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { dashboardApi } from '@/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { KpiGrid } from '@/components/ui/KpiGrid';
import { StatCard } from '@/components/ui/StatCard';
import { DashboardSkeleton } from '@/components/ui/Skeleton';
import { ChartCard } from '@/components/ui/ChartCard';
import { formatCurrency } from '@/utils/format';
import type { MasterAdminDashboardStats } from '@/types/dashboard';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { showApiError } from '@/utils/apiError';

export function MasterDashboardPage() {
  const [stats, setStats] = useState<MasterAdminDashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    dashboardApi
      .masterStats()
      .then(setStats)
      .catch((err) => showApiError(err, 'Failed to load dashboard'))
      .finally(() => setLoading(false));
  }, []);

  const chartData = stats
    ? [
        { name: 'Orgs', value: stats.totalOrganizations },
        { name: 'Active', value: stats.activeOrganizations },
        { name: 'Subs', value: stats.activeSubscriptions },
      ]
    : [];

  if (loading) {
    return (
      <div className="page-content">
        <PageHeader title="Master Dashboard" description="Platform-wide overview" />
        <DashboardSkeleton kpiCount={6} />
      </div>
    );
  }

  return (
    <div className="page-content">
      <PageHeader title="Master Dashboard" description="Platform-wide overview" />
      <KpiGrid columns={4}>
        <StatCard title="Total Organizations" value={stats?.totalOrganizations ?? '—'} icon={<BusinessIcon />} />
        <StatCard title="Active Organizations" value={stats?.activeOrganizations ?? '—'} icon={<BusinessIcon />} />
        <StatCard title="Active Subscriptions" value={stats?.activeSubscriptions ?? '—'} icon={<SubscriptionsIcon />} />
        <StatCard
          title="Today's Platform Sales"
          value={stats ? formatCurrency(stats.todaySalesPlatform) : '—'}
          icon={<TrendingUpIcon />}
        />
        <StatCard title="Today's Invoices" value={stats?.todayInvoicesPlatform ?? '—'} icon={<ReceiptIcon />} />
        <StatCard
          title="Month Sales"
          value={stats ? formatCurrency(stats.monthSalesPlatform) : '—'}
          icon={<TrendingUpIcon />}
        />
      </KpiGrid>

      <ChartCard title="Organization metrics" description="Active tenants and subscriptions">
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#64748b' }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 12, fill: '#64748b' }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13 }} />
            <Bar dataKey="value" fill="#7c3aed" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
    </div>
  );
}
